import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Sparkles, AlertCircle } from 'lucide-react';
import api from '../../services/api';

// Generic template field (from /templates/fields)
interface TemplateField {
    id: string;
    field_name: string;
    field_label: string;
    field_type: string;
    field_category: string;
    description: string;
    example: string;
    placeholder: string;
    is_required: boolean;
    options?: string[];
    order: number;
}

// Dynamic question derived from agent-detected missing info
interface MissingInfoItem {
    topic: string;
    question: string;
    impact: string;
    suggested_value?: string;
}

// Unified "step" structure used internally
interface WizardStep {
    field_name: string;
    field_label: string;
    field_type: 'textarea' | 'text';
    field_category: string;
    description: string;
    example: string;
    placeholder: string;
    is_required: boolean;
    impact?: string;
}

interface MissingInfoWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (values: Record<string, string>) => void;
    projectId: string;
    /** If provided, the wizard creates steps from these items instead of generic templates */
    missingInfo?: MissingInfoItem[];
}

function missingInfoToStep(item: MissingInfoItem): WizardStep {
    const key = item.topic.toLowerCase().replace(/\s+/g, '_');
    return {
        field_name: key,
        field_label: item.topic,
        field_type: 'textarea',
        field_category: item.impact === 'blocking' ? 'critical' : 'context',
        description: item.question,
        example: item.suggested_value || '',
        placeholder: `Your answer for "${item.topic}"...`,
        is_required: item.impact === 'blocking' || item.impact === 'high',
        impact: item.impact,
    };
}

function templateFieldToStep(f: TemplateField): WizardStep {
    return {
        field_name: f.field_name,
        field_label: f.field_label,
        field_type: f.field_type as 'textarea' | 'text',
        field_category: f.field_category,
        description: f.description,
        example: f.example,
        placeholder: f.placeholder,
        is_required: f.is_required,
    };
}

const impactColor: Record<string, string> = {
    blocking: 'text-red-400',
    high: 'text-orange-400',
    important: 'text-yellow-400',
};

const MissingInfoWizard: React.FC<MissingInfoWizardProps> = ({
    isOpen,
    onClose,
    onSave,
    missingInfo,
}) => {
    const [steps, setSteps] = useState<WizardStep[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [values, setValues] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            loadSteps();
        }
    }, [isOpen]);

    const loadSteps = async () => {
        setIsLoading(true);
        setCurrentStep(0);
        setValues({});

        try {
            // Priority 1: Use AI-detected missing_info (dynamic & context-aware)
            if (missingInfo && missingInfo.length > 0) {
                setSteps(missingInfo.map(missingInfoToStep));
                return;
            }

            // Priority 2: Fall back to generic template fields
            const fields = await api.getTemplateFields();
            setSteps(fields.map(templateFieldToStep));
        } catch (error) {
            console.error('Error loading wizard steps:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const current = steps[currentStep];
    const progress = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;
    const isDynamic = missingInfo && missingInfo.length > 0;

    const handleNext = () => {
        if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    };

    const handlePrevious = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const handleSave = () => {
        onSave(values);
        setValues({});
        setCurrentStep(0);
        onClose();
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-800">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary" />
                            Missing Info Wizard
                            {isDynamic && (
                                <span className="text-xs font-normal px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full ml-1">
                                    Contextual
                                </span>
                            )}
                        </h2>
                        <p className="text-sm text-slate-400 mt-1">
                            Step {currentStep + 1} of {steps.length}
                            {isDynamic && (
                                <span className="ml-2 text-slate-500">· Based on AI analysis</span>
                            )}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="px-6 pt-4">
                    <div className="w-full bg-slate-800 rounded-full h-2">
                        <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        </div>
                    ) : steps.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-slate-400">No missing information detected</p>
                        </div>
                    ) : current ? (
                        <div className="space-y-5">
                            {/* Label */}
                            <div>
                                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                    {current.field_label}
                                    {current.is_required && <span className="text-red-400 text-sm">*</span>}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                                        {current.field_category}
                                    </span>
                                    {current.impact && (
                                        <span className={`text-xs font-medium ${impactColor[current.impact] || 'text-slate-400'}`}>
                                            · Impact: {current.impact}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Description */}
                            {current.description && (
                                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                                    <p className="text-slate-300 text-sm">{current.description}</p>
                                </div>
                            )}

                            {/* Example */}
                            {current.example && (
                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                    <p className="text-xs text-slate-400 mb-1">Example:</p>
                                    <p className="text-blue-400 text-sm">{current.example}</p>
                                </div>
                            )}

                            {/* AI suggestion hint */}
                            {isDynamic && !current.example && (
                                <div className="flex items-start gap-2 text-slate-500 text-xs">
                                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                    <span>No prior context found. Your answer will be used to improve test case generation.</span>
                                </div>
                            )}

                            {/* Input */}
                            <div>
                                {current.field_type === 'textarea' ? (
                                    <textarea
                                        value={values[current.field_name] || ''}
                                        onChange={(e) =>
                                            setValues({ ...values, [current.field_name]: e.target.value })
                                        }
                                        placeholder={current.placeholder}
                                        rows={4}
                                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary resize-none"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={values[current.field_name] || ''}
                                        onChange={(e) =>
                                            setValues({ ...values, [current.field_name]: e.target.value })
                                        }
                                        placeholder={current.placeholder}
                                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary"
                                    />
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-slate-800">
                    <button
                        onClick={handlePrevious}
                        disabled={currentStep === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                    </button>

                    <div className="flex gap-2">
                        {currentStep < steps.length - 1 ? (
                            <button
                                onClick={handleNext}
                                disabled={current?.is_required && !values[current.field_name]?.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSave}
                                disabled={current?.is_required && !values[current.field_name]?.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                <Check className="w-4 h-4" />
                                Save
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MissingInfoWizard;
